// ============================================================
// Logo Animation Mockups - Taska Ummu Qaseh
// 3 mockup animasi logo berbeza untuk pratonton & pilihan
// Mockup 1: Orbit Bintang | Mockup 2: Belon Terapung | Mockup 3: Jelly Pop
// ============================================================

import React from "react";
import { Sparkles } from "lucide-react";

// ---------- Mockup 1: Orbit Bintang ----------
// Logo terapung perlahan + bintang kecil mengorbit + cincin cahaya berputar
const OrbitStarLogo = ({ src, alt, className }) => (
  <div className="relative inline-flex items-center justify-center">
    <style>{`
      @keyframes mock1-float {
        0%, 100% { transform: translateY(0); }
        50% { transform: translateY(-10px); }
      }
      @keyframes mock1-orbit {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
      @keyframes mock1-ring {
        0% { transform: rotate(0deg) scale(1); opacity: 0.7; }
        50% { transform: rotate(180deg) scale(1.06); opacity: 1; }
        100% { transform: rotate(360deg) scale(1); opacity: 0.7; }
      }
      @keyframes mock1-twinkle {
        0%, 100% { opacity: 0.4; transform: scale(0.7); }
        50% { opacity: 1; transform: scale(1.2); }
      }
      .mock1-logo { animation: mock1-float 3s ease-in-out infinite; }
      .mock1-orbit-wrap { animation: mock1-orbit 8s linear infinite; }
      .mock1-ring {
        animation: mock1-ring 6s linear infinite;
        border: 3px dashed rgba(255, 201, 60, 0.75);
      }
      .mock1-star { animation: mock1-twinkle 1.6s ease-in-out infinite; }
    `}</style>

    {/* Cincin cahaya berputar */}
    <div className="mock1-ring absolute inset-[-14px] rounded-full pointer-events-none" />

    {/* Bintang mengorbit */}
    <div className="mock1-orbit-wrap absolute inset-[-26px] pointer-events-none">
      {[0, 90, 180, 270].map((deg, i) => (
        <div
          key={deg}
          className="absolute left-1/2 top-1/2"
          style={{ transform: `rotate(${deg}deg) translateX(90px)` }}
        >
          <Sparkles
            className="mock1-star w-5 h-5"
            style={{
              color: ["#FFC93C", "#FF6B9D", "#6FE7DD", "#B39DFF"][i],
              animationDelay: `${i * 0.4}s`,
            }}
          />
        </div>
      ))}
    </div>

    <img src={src} alt={alt} className={`${className} mock1-logo`} />
  </div>
);

// ---------- Mockup 2: Belon Terapung ----------
// Logo naik turun lembut + goyang kiri kanan seperti belon + bayang mengecil/membesar
const BalloonLogo = ({ src, alt, className }) => (
  <div className="relative inline-flex flex-col items-center justify-center">
    <style>{`
      @keyframes mock2-balloon {
        0%, 100% { transform: translateY(0) rotate(-2.5deg); }
        25% { transform: translateY(-12px) rotate(0deg); }
        50% { transform: translateY(-18px) rotate(2.5deg); }
        75% { transform: translateY(-12px) rotate(0deg); }
      }
      @keyframes mock2-shadow {
        0%, 100% { transform: scaleX(1); opacity: 0.35; }
        50% { transform: scaleX(0.65); opacity: 0.18; }
      }
      .mock2-logo {
        animation: mock2-balloon 4s ease-in-out infinite;
        transform-origin: bottom center;
      }
      .mock2-shadow {
        animation: mock2-shadow 4s ease-in-out infinite;
        background: radial-gradient(ellipse at center, rgba(60,50,110,0.55) 0%, transparent 70%);
      }
    `}</style>

    <img src={src} alt={alt} className={`${className} mock2-logo`} />

    {/* Bayang di bawah */}
    <div className="mock2-shadow w-24 h-3 rounded-full mt-1" />
  </div>
);

// ---------- Mockup 3: Jelly Pop ----------
// Squash & stretch ceria + pop berkala + kilauan kecil muncul ketika pop
const JellyPopLogo = ({ src, alt, className }) => (
  <div className="relative inline-flex items-center justify-center">
    <style>{`
      @keyframes mock3-jelly {
        0%, 100% { transform: scale(1, 1); }
        12% { transform: scale(1.12, 0.88); }
        24% { transform: scale(0.92, 1.08); }
        36% { transform: scale(1.06, 0.94); }
        48% { transform: scale(0.98, 1.02); }
        60% { transform: scale(1, 1); }
      }
      @keyframes mock3-pop-glow {
        0%, 55%, 100% { opacity: 0; transform: scale(0.6); }
        62% { opacity: 0.9; transform: scale(1.15); }
        75% { opacity: 0; transform: scale(1.4); }
      }
      .mock3-logo {
        animation: mock3-jelly 2.6s ease-in-out infinite;
        transform-origin: center bottom;
      }
      .mock3-glow {
        animation: mock3-pop-glow 2.6s ease-in-out infinite;
        background: radial-gradient(circle, rgba(255,201,60,0.55) 0%, rgba(255,107,157,0.25) 50%, transparent 70%);
      }
    `}</style>

    {/* Kilauan pop */}
    <div className="mock3-glow absolute inset-[-16px] rounded-full pointer-events-none" />

    <img src={src} alt={alt} className={`${className} mock3-logo`} />
  </div>
);

export const LOGO_MOCKUPS = [
  { id: 1, name: "Orbit Bintang", Component: OrbitStarLogo },
  { id: 2, name: "Belon Terapung", Component: BalloonLogo },
  { id: 3, name: "Jelly Pop", Component: JellyPopLogo },
];

/**
 * Paparan logo mengikut mockup dipilih.
 * mockup = 0 (asal/bounce sedia ada tidak digunakan di sini), 1, 2, 3
 */
export const MockupLogo = ({ mockup, src, alt, className }) => {
  const found = LOGO_MOCKUPS.find((m) => m.id === mockup);
  if (!found) return null;
  const { Component } = found;
  return <Component src={src} alt={alt} className={className} />;
};

export default MockupLogo;
